import { useQuery } from '@tanstack/react-query';
import { parseISO } from 'date-fns';
import { useEffect, useState } from 'preact/hooks';
import { useRouter } from 'preact-router';
import { API_BASE_URL } from 'Shared/fetch';
import useFetch, { CachePolicies } from 'use-http';

import { useLatestActiveTime } from '@/hooks/useLatestActiveDateTime';
import { getEventsQueryOptions } from '@/queries/events';
import { Event } from '@/queries/events/types';
import { getHabitatSchedulesOptions } from '@/queries/habitats';
import { HabitatCurrentOrUpcomingSchedule } from '@/queries/habitats/types';

const hydrateTalk = ({ _id, zoo, startTime, stopTime, habitat, camera, title }: HabitatCurrentOrUpcomingSchedule) => ({
  _id,
  zoo,
  title,
  startTime: parseISO(startTime),
  stopTime: parseISO(stopTime),
  profileImage: habitat?.profileImage,
  link: `/h/${habitat?.zoo?.slug}/${habitat?.slug}`,
  habitatId: habitat?._id,
  description: habitat?.description,
  isStreamLive: !!camera && camera.cameraStatus !== 'off',
});

type HydratedTalk = ReturnType<typeof hydrateTalk>;

export const useUpcomingTalks = (habitatId: string, quantity = 3) => {
  const [upcoming, setUpcoming] = useState<HydratedTalk[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<boolean | undefined>();

  const { get, response } = useFetch(API_BASE_URL, {
    credentials: 'include',
    cachePolicy: CachePolicies.NO_CACHE,
  });

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(false);

      const params = new URLSearchParams({ limit: quantity.toString() });
      await get(habitatId ? `/habitats/${habitatId}/schedules/upcoming?${params}` : `/schedules/upcoming?${params}`);

      if (!response.ok) {
        setError(true);
        // TODO: log it
        return;
      }

      const now = new Date();
      const list = ((response.data.events as HabitatCurrentOrUpcomingSchedule[]).map(hydrateTalk) as HydratedTalk[]).filter(
        ({ startTime, isStreamLive }) => startTime > now || isStreamLive,
      );

      setUpcoming(list);
      setLoading(false);
    };

    // we need explicit null for habitat ID to
    // know if the hook should run without this value
    if (habitatId || habitatId === null) {
      fetchData();

      const interval = setInterval(
        () => {
          fetchData();
        },
        5 * 60 * 1000,
      );

      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [habitatId]);

  return { loading, error, upcoming };
};

export const useCurrentAndUpcomingTalks = (habitatId: string, limit: number) => {
  return useQuery({
    ...getHabitatSchedulesOptions(habitatId, { limit }),
    select: (data) => {
      const now = new Date();
      const talks = data.events.map(hydrateTalk).filter(({ stopTime }) => stopTime > now);
      return {
        currentTalk: talks.find(({ startTime }) => startTime < now),
        upcoming: talks.filter(({ startTime }) => startTime > now),
      };
    },
  });
};

export const useCurrentStreamEvent = (habitatId: string | null | undefined) => {
  const [time, reload] = useLatestActiveTime();
  const [lastFetchedSchedule, setLastFetchedSchedule] = useState<Event | null>(null);
  const query = useQuery({
    ...getEventsQueryOptions({
      habitatIds: habitatId ? [habitatId] : [],
      startTime: time,
      events: ['live'],
      pageSize: 1,
    }),
    select: (data) => data?.[0],
    enabled: !!habitatId,
  });

  const { data, isSuccess, isLoading } = query;

  const schedule = data ?? (isLoading && lastFetchedSchedule?.habitatId === habitatId ? lastFetchedSchedule : undefined);

  useEffect(() => {
    if (!schedule?.stopTime) {
      return undefined;
    }
    const id = setTimeout(
      () => {
        reload();
      },
      Date.parse(schedule.stopTime) - Date.now(),
    );
    return () => {
      clearTimeout(id);
    };
  }, [schedule, reload]);

  useEffect(() => {
    const delay = schedule?.startTime ? Date.parse(schedule.startTime) - Date.now() : -1;
    if (delay < 0) {
      return undefined;
    }
    const id = setTimeout(() => {
      reload();
    }, delay);
    return () => {
      clearTimeout(id);
    };
  }, [schedule, reload]);

  useEffect(() => {
    if (isSuccess) {
      setLastFetchedSchedule(data);
    }
  }, [data, isSuccess]);

  // reset the last fetched schedule when the habitat ID changes
  useEffect(() => {
    setLastFetchedSchedule(null);
  }, [habitatId]);

  return [schedule, time, query] as const;
};

export const useCurrentEvent = (habitatId: string, isHostStreamOn: boolean) => {
  const [time, reload] = useLatestActiveTime();
  const [{ matches }] = useRouter();

  const query = useQuery({
    ...getEventsQueryOptions({
      habitatIds: [habitatId],
      // if host stream is on, we query 10 minutes before the start time to allow the host is overrun
      startTime: isHostStreamOn ? time - 600_000 : time,
      endTime: time + 5_000,
      pageSize: 1,
    }),
    select: (data) => data[0] ?? null,
  });

  const { data, isLoading } = query;

  // cache the event to avoid flickering during loading
  const [cachedEvent, setCachedEvent] = useState(data);
  const event = isLoading ? cachedEvent : data;
  const isActive = !!(
    event &&
    Date.parse(event.startTime) <= time &&
    Date.parse(event.stopTime) > (isHostStreamOn ? time - 600_000 : time)
  );

  const activeEvent = isActive ? event : undefined;

  useEffect(() => {
    if (!isLoading) {
      setCachedEvent(data);
    }
  }, [data, isLoading]);

  // reload when host stream is turned on or off
  useEffect(() => {
    reload();
  }, [isHostStreamOn, reload]);

  // reload when chat tab is active
  useEffect(() => {
    if (matches?.tab === 'chat') {
      reload();
    }
  }, [matches?.tab, reload]);

  // reload when event ends to update the UI
  useEffect(() => {
    if (isActive && activeEvent?.stopTime && !isHostStreamOn) {
      const endTime = Date.parse(activeEvent.stopTime);
      const id = setTimeout(reload, Math.max(0, endTime - Date.now() + 1000)); // add 1 second to avoid edge case
      return () => clearTimeout(id);
    }
    return undefined;
  }, [isActive, activeEvent?.stopTime, reload, isHostStreamOn]);

  return { event: activeEvent, query };
};
